import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageSEO } from '@/hooks/usePageSEO';
import { useJsonLd } from '@/hooks/useJsonLd';
import { getPostBySlug } from '@/lib/blog';
import { DEFAULT_LOCALE, SITE_URL, blogPostRoute, type PublicRoute } from '@/lib/seo/public-routes';
import { articleJsonLd, breadcrumbJsonLd } from '@/lib/seo/structured-data';
import { pageMetadata } from '@/lib/seo/metadata';
import { findPublicRoute } from '@/lib/seo/site-routes';

const PROSE_CLASSES = [
  '[&_h2]:mt-10 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:text-foreground',
  '[&_h3]:mt-8 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:tracking-tight [&_h3]:text-foreground',
  '[&_p]:mt-4 [&_p]:text-[15px] [&_p]:leading-relaxed [&_p]:text-muted-foreground',
  '[&_ul]:mt-4 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 [&_ul]:text-[15px] [&_ul]:leading-relaxed [&_ul]:text-muted-foreground',
  '[&_ol]:mt-4 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-5 [&_ol]:text-[15px] [&_ol]:leading-relaxed [&_ol]:text-muted-foreground',
  '[&_a]:text-lime [&_a]:underline [&_a]:underline-offset-4',
  '[&_strong]:font-semibold [&_strong]:text-foreground',
  '[&_code]:mono [&_code]:rounded [&_code]:bg-surface-2 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[13px] [&_code]:text-foreground',
  '[&_pre]:mono [&_pre]:mt-4 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:border [&_pre]:border-hairline [&_pre]:bg-surface-1 [&_pre]:p-4 [&_pre]:text-[13px] [&_pre]:leading-relaxed',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-inherit',
  '[&_table]:mt-4 [&_table]:block [&_table]:w-full [&_table]:overflow-x-auto [&_table]:border-collapse [&_table]:text-[13px]',
  '[&_th]:border [&_th]:border-hairline [&_th]:bg-surface-1 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-medium [&_th]:text-foreground',
  '[&_td]:border [&_td]:border-hairline [&_td]:px-3 [&_td]:py-2 [&_td]:align-top [&_td]:text-muted-foreground',
  '[&_img]:mt-6 [&_img]:w-full [&_img]:rounded-xl [&_img]:border [&_img]:border-hairline',
].join(' ');

function formatDate(date: string) {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * The not-found state is a real page but never a registry route. It runs
 * through the same metadata generator so its social tags are consistent, and it
 * canonicalises to the blog index rather than to a URL that has no post.
 */
function missingPostRoute(slug: string | undefined): PublicRoute {
  return {
    path: `/blog/${slug ?? ''}`,
    routePattern: '/blog/:slug',
    pageType: 'blog-post',
    locale: DEFAULT_LOCALE,
    indexable: false,
    canonicalPath: '/blog',
    alternates: [],
    metadata: {
      title: 'Post not found – DebtDrone Blog',
      description: 'This blog post could not be found.',
    },
    sitemap: { include: false },
  };
}

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = getPostBySlug(slug);

  // Published posts carry a registry route, so their metadata matches the
  // sitemap and prerendered snapshot exactly; an unknown slug renders the
  // not-found state and has no registry entry.
  const route = findPublicRoute(`/blog/${slug ?? ''}`);
  const seo = useMemo(() => {
    if (route) return pageMetadata(route);
    // A draft previewed during development renders without a registry route;
    // derive its metadata the same way so the preview matches what publishing
    // would produce.
    if (post) return pageMetadata(blogPostRoute(post.frontmatter));
    return pageMetadata(missingPostRoute(slug));
  }, [route, post, slug]);

  usePageSEO(seo);

  const articleStructuredData = useMemo(
    () =>
      post
        ? articleJsonLd({
            url: `${SITE_URL}/blog/${post.frontmatter.slug}`,
            headline: post.frontmatter.title,
            description: post.frontmatter.excerpt,
            datePublished: post.frontmatter.date,
          })
        : null,
    [post],
  );
  useJsonLd('blog-post', articleStructuredData);

  // Mirrors the breadcrumb rendered above the heading, 1:1.
  const breadcrumbStructuredData = useMemo(
    () =>
      post
        ? breadcrumbJsonLd([
            { name: 'Home', url: `${SITE_URL}/` },
            { name: 'Blog', url: `${SITE_URL}/blog` },
            { name: post.frontmatter.title },
          ])
        : null,
    [post],
  );
  useJsonLd('blog-post-breadcrumb', breadcrumbStructuredData);

  if (!post) {
    return (
      <div className="bg-background py-24 sm:py-32">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Post not found</h1>
          <p className="mt-2 text-lg leading-8 text-muted-foreground">
            We couldn't find that post — it may have been moved or removed.
          </p>
          <Link to="/blog" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-lime">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to blog
          </Link>
        </div>
      </div>
    );
  }

  const { Component, frontmatter } = post;

  return (
    <div className="bg-background py-24 sm:py-32">
      <div className="mx-auto max-w-3xl px-6 lg:px-8">
        <nav aria-label="Breadcrumb" className="mono text-[11px] uppercase tracking-wider text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-x-2">
            <li>
              <Link to="/" className="transition-colors hover:text-foreground">Home</Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>
              <Link to="/blog" className="transition-colors hover:text-foreground">Blog</Link>
            </li>
            <li aria-hidden="true">/</li>
            <li aria-current="page" className="text-foreground">{frontmatter.title}</li>
          </ol>
        </nav>
        <div className="mono mt-6 text-[11px] uppercase tracking-wider text-muted-foreground">
          {formatDate(frontmatter.date)}
        </div>
        <h1 className="mt-2 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{frontmatter.title}</h1>
        <article className={PROSE_CLASSES}>
          <Component />
        </article>
      </div>
    </div>
  );
};

export default BlogPost;
